import {useCallback, useMemo, useRef} from 'react'

import {openAlert} from '../../../../../../../_metronic/elements/NotificationAlert'
import {
  buildImageLayerCacheOptions,
  buildImageLayerRequestKey,
} from './remoteSensingLayerRequest'
import {normalizeRemoteSensingSource} from './remoteSensingModel'

const SENTINEL_PROVIDER_NOT_CONFIGURED = 'sentinel_provider_not_configured'
const MAX_PROVIDER_ERROR_JSON_BYTES = 16 * 1024

const getResponseContentType = (response) => String(
  response?.headers?.['content-type'] ||
  response?.headers?.get?.('content-type') ||
  response?.data?.type ||
  ''
).split(';', 1)[0].trim().toLowerCase()

const isJsonContentType = (contentType) => (
  contentType === 'application/json' || contentType.endsWith('+json')
)

const readBlobAsText = (blob) => {
  if (typeof blob.text === 'function') {
    return blob.text()
  }

  if (typeof FileReader === 'undefined') {
    return Promise.resolve(null)
  }

  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null)
    reader.onerror = () => resolve(null)
    reader.readAsText(blob)
  })
}

const hasSentinelProviderNotConfiguredCode = async (error) => {
  const response = error?.response
  if (response?.status !== 503) {
    return false
  }

  if (response.data?.code === SENTINEL_PROVIDER_NOT_CONFIGURED) {
    return true
  }

  const isBlob = typeof Blob !== 'undefined' && response.data instanceof Blob
  if (
    !isBlob ||
    response.data.size > MAX_PROVIDER_ERROR_JSON_BYTES ||
    !isJsonContentType(getResponseContentType(response))
  ) {
    return false
  }

  try {
    const text = await readBlobAsText(response.data)
    if (text == null || text.length > MAX_PROVIDER_ERROR_JSON_BYTES) {
      return false
    }

    return JSON.parse(text)?.code === SENTINEL_PROVIDER_NOT_CONFIGURED
  } catch (parseError) {
    return false
  }
}

export const useStableImageLayerInputs = ({
  acquisition,
  date,
  enhanced = false,
  imageType,
  land,
}) => {
  const nextCacheOptions = buildImageLayerCacheOptions({
    acquisition,
    date,
    enhanced,
    imageType,
    land,
  })
  const nextSource = normalizeRemoteSensingSource(nextCacheOptions.source, imageType)
  const sourceImageType = nextSource.imageType
  const sourceLayer = nextSource.layer
  const sourceProduct = nextSource.product
  const sourceProvider = nextSource.provider
  const sourceQuality = nextSource.quality
  const source = useMemo(() => ({
    imageType: sourceImageType,
    layer: sourceLayer,
    product: sourceProduct,
    provider: sourceProvider,
    quality: sourceQuality,
  }), [
    sourceImageType,
    sourceLayer,
    sourceProduct,
    sourceProvider,
    sourceQuality,
  ])
  const cacheAlgorithmVersion = nextCacheOptions.algorithmVersion
  const cacheAccessId = nextCacheOptions.accessId
  const cacheCompanyPeriodId = nextCacheOptions.companyPeriodId
  const cacheDate = nextCacheOptions.date
  const cacheEnhanced = nextCacheOptions.enhanced
  const cacheGeometryHash = nextCacheOptions.geometryHash
  const cacheImageType = nextCacheOptions.imageType
  const cacheSceneId = nextCacheOptions.sceneId
  const cacheOptions = useMemo(() => ({
    accessId: cacheAccessId,
    companyPeriodId: cacheCompanyPeriodId,
    algorithmVersion: cacheAlgorithmVersion,
    date: cacheDate,
    enhanced: cacheEnhanced,
    geometryHash: cacheGeometryHash,
    imageType: cacheImageType,
    sceneId: cacheSceneId,
    source,
  }), [
    cacheAccessId,
    cacheCompanyPeriodId,
    cacheAlgorithmVersion,
    cacheDate,
    cacheEnhanced,
    cacheGeometryHash,
    cacheImageType,
    cacheSceneId,
    source,
  ])

  const hasAcquisition = acquisition != null
  const acquisitionId = acquisition?.acquisitionId || null
  const accessId = acquisition?.accessId || null
  const companyPeriodId = acquisition?.companyPeriodId || null
  const algorithmVersion = acquisition?.algorithmVersion || null
  const geometryHash = acquisition?.geometryHash || null
  const itemId = acquisition?.itemId || null
  const stableAcquisition = useMemo(() => hasAcquisition ? {
    accessId,
    companyPeriodId,
    acquisitionId,
    algorithmVersion,
    geometryHash,
    itemId,
    source,
  } : null, [
    accessId,
    companyPeriodId,
    acquisitionId,
    algorithmVersion,
    geometryHash,
    hasAcquisition,
    itemId,
    source,
  ])

  return {
    cacheOptions,
    stableAcquisition,
  }
}

export const useImageLayerAttempt = ({
  cacheOptions,
  errorHandler,
  landId,
  retryVersion = 0,
}) => {
  const errorHandlerRef = useRef(errorHandler)
  errorHandlerRef.current = errorHandler

  const requestKey = useMemo(
    () => buildImageLayerRequestKey({landId, ...cacheOptions}),
    [cacheOptions, landId]
  )
  const attemptKey = useMemo(
    () => `${requestKey}:retry-${retryVersion}`,
    [requestKey, retryVersion]
  )
  const handleRequest = useCallback(async (request) => {
    try {
      return await request
    } catch (error) {
      if (await hasSentinelProviderNotConfiguredCode(error)) {
        openAlert(
          'danger',
          'Sentinel no está configurado',
          'Este entorno no tiene credenciales activas de Sentinel Hub. Contactá a un administrador y volvé a intentar.'
        )

        return {
          ok: false,
          error_type: SENTINEL_PROVIDER_NOT_CONFIGURED,
          data: {code: SENTINEL_PROVIDER_NOT_CONFIGURED},
        }
      }

      return errorHandlerRef.current(Promise.reject(error))
    }
  }, [])

  return {
    attemptKey,
    handleRequest,
    requestKey,
  }
}
